<template>
  <div style="position: relative;">
    <v-progress-linear style="position: absolute;" class="ma-0" indeterminate color="info" v-if="loading" />
    <v-alert type="warning" outline class="ma-0" :value="hasError">
      <div class="primary--text" v-html="errorHtml" />
    </v-alert>
    <div :id="id" />
  </div>
</template>

<script>
  import Highcharts from 'highcharts'
  import DataCharts from '@/components/mixins/DataCharts'
  import axios from 'axios'
  import moment from 'moment'
  import {
    format,
  } from 'date-fns'

  export default {
    mixins: [DataCharts],
    props: ['requestData'],
    data() {
      return {
        loading: false,
        hasError: false,
        errorHtml: 'Oops, there\'s no data available.',
      }
    },
    computed: {
      timeRange() {
        if (this.requestData != null) {
          return [this.requestData.startDate, this.requestData.endDate]
        }
        return null
      },
      datasets() {
        return this.requestData.datasets
      },
    },
    watch: {
      requestData() {
        this.generateGraph()
      },
    },
    methods: {
      processArgsAndData(args, timeSeriesData) {
        args.yAxis = args.yAxis.filter(x => x.title.text.indexOf('Historic') < 0)
        var dischargeIndex = args.yAxis.findIndex(x => x.title.text.indexOf('Discharge (m³/s)') >= 0)
        //console.log('discharge time series index ' + dischargeIndex)
        var historicalRangeDataset = {
          name: 'Historical Range',
          type: 'arearange',
          color: '#808080',
          fillOpacity: 0.3,
          zIndex: 0,
          unit: 'm³/s',
          marker: {
            enabled: false,
          },
          yAxis: dischargeIndex,
        }
        args.datasets = args.datasets.filter(x => x.name.indexOf('Historic') < 0)
        args.datasets.push(historicalRangeDataset)

        var minData = timeSeriesData.find(x => x.name.indexOf('Historic Discharge Minimum') >= 0)
        var maxData = timeSeriesData.find(x => x.name.indexOf('Historic Discharge Maximum') >= 0)
        var ranges = []
        for (var i = 0; i < minData.data.length; i++) {
          ranges.push([minData.data[i][0],
            minData.data[i][1],
            maxData.data[i][1],
          ])
        }
        var historicalRangeData = {
          color: '#808080',
          data: ranges,
          name: 'Historic Range',
          yAxis: dischargeIndex,
          type: 'arearange',
          unit: 'm³/s',
        }
        timeSeriesData = timeSeriesData.filter(x => x.name.indexOf('Historic') < 0)
        timeSeriesData.push(historicalRangeData)

        for (var j = 0; j < timeSeriesData.length; j++) {
          if (timeSeriesData[j].name != 'Historic Range') {
            timeSeriesData[j].yAxis = args.yAxis.findIndex(x => timeSeriesData[j].name.indexOf(x.title.text.substring(x
              .title.text.indexOf('.') + 1, x.title.text.indexOf('@'))))
          }
        }
        return {
          args: args,
          timeSeriesData: timeSeriesData,
        }
      },
      generateGraph() {
        if (this.datasets != undefined) {
          this.loading = true
          this.hasError = false
          try {
            var args = this.constructGraphingArguments()
            this.fetchDataFroGraph(
                args.datasets,
                args.timeRange[0],
                args.timeRange[1],
                this.requestData.IncludeGapMarkers
              )
              .then(seriesData => {
                //var processedArgs = this.processArgs(args)
                var dischargeIndex = args.datasets.findIndex(x => x.name.indexOf('Discharge (m³/s)') >= 0)
                var historicalMaxIndex = args.datasets.findIndex(x => x.name.indexOf('Historic Discharge Maximum') >=
                  0)
                var historicalMinIndex = args.datasets.findIndex(x => x.name.indexOf('Historic Discharge Minimum') >=
                  0)

                //Has discharge data
                if (dischargeIndex >= 0 && historicalMaxIndex >= 0 && historicalMinIndex >= 0) {
                  var processResult = this.processArgsAndData(args, seriesData)
                  args = processResult.args
                  seriesData = processResult.timeSeriesData
                  this.drawChartWithArgs(processResult.args, processResult.timeSeriesData, {
                    shared: true,
                    valueSuffix: ' m³/s',
                  })
                } else {
                  this.drawChartWithArgs(args, seriesData)
                }
                for (var item of document.getElementsByClassName('highcharts-autoscale-button')) {
                  item.dispatchEvent(new CustomEvent('click'))
                }
                this.loading = false
              })
              .catch(() => {
                this.hasError = true
                this.loading = false
                this.errorHtml =
                  'Oops, we couldn\'t plot the data points to the graph. Please let us know so we can fix it.'
              })
          } catch (e) {
            this.hasError = true
            this.loading = false
            this.errorHtml =
              'Uh oh, we couldn\'t fetch the data to generate the graph. Try again later &mdash; let us know if it happens again.'
          }
        }
      },
      constructArgumentForSingleDataset(dataset, index, oppositeYAxis) {
        var timeSeriesColor = Highcharts.getOptions().colors[index % 10]
        var dataSetType = this.decideChartType(dataset.DataSetIdentifer)
        var dataSetConfig = {
          name: dataset.DataSetIdentifer,
          type: dataSetType,
          yAxis: index,
          color: timeSeriesColor,
        }
        var yAxisConfig = {
          // Primary yAxis
          labels: {
            format: '',
            style: {
              color: timeSeriesColor,
            },
          },
          title: {
            text: dataset.DataSetIdentifer,
            style: {
              color: timeSeriesColor,
            },
          },
          opposite: oppositeYAxis,
        }

        return {
          dataset: dataSetConfig,
          yAxis: yAxisConfig,
        }
      },
      constructGraphingArguments() {
        var counter = -1
        var datasets = []
        var yAxis = []
        var opposite = true
        if (this.datasets != null && this.datasets.length > 5) {
          this.hasError = true
          this.errorHtml = 'Select at most 5 analytes to graph.'
        }
        for (var i = 0; i < this.datasets.length; i++) {
          var matchedDataset = this.datasets[i]
          if (matchedDataset != null) {
            counter++
            var config = this.constructArgumentForSingleDataset(
              matchedDataset,
              counter,
              opposite
            )
            datasets.push(config.dataset)
            yAxis.push(config.yAxis)
            opposite = !opposite
          }
        }
        if (counter >= 0) {
          // find the latest time range
          var graphingArguments = {
            title: '',
            timeRange: this.timeRange,
            datasets: datasets,
            yAxis: yAxis,
          }
          return graphingArguments
        }
        return null
      },
      async fetchDataFroGraph(datasetsArgs, startDate, endDate, includeGapMarkers) {
        var data = []
        var url =
          'api/GraphAPI?startDate=' +
          format(startDate, 'YYYY-MM-DD') +
          '&endDate=' +
          format(endDate, 'YYYY-MM-DD') +
          '&includeTime=false&tryCompression=true&includeGapMarkers=' + includeGapMarkers
        await axios.post(url, this.datasets).then(res => {
          for (var i = 0; i < res.data.length; i++) {
            var dataForSeries = res.data[i]
            var rows = dataForSeries.Rows.map(x => [
              moment(x.DateTime).valueOf(),
              x.Value,
            ])
            var approvalLevels = dataForSeries.Rows.map(x => x.ApprovalLevel)
            var dataGrades = dataForSeries.Rows.map(x => x.Grade)
            var dataQualifiers = dataForSeries.Rows.map(x => x.Qualifier)
            var datasetArgsMatched = datasetsArgs.filter(x => x.name === res.data[i].DataSetIdentifer)[0]
            if (!datasetArgsMatched) return null
            data.push({
              name: dataForSeries.DataSetName,
              data: rows,
              dataQaQc: approvalLevels,
              dataGrades: dataGrades,
              dataQualifiers: dataQualifiers,
              yAxis: datasetArgsMatched.yAxis,
              type: datasetArgsMatched.type,
              unit: dataForSeries.Unit,
              color: datasetArgsMatched.color,
            })
          }
        })
        return data
      },
    },
  }
</script>